import { AppPaths } from "@p0security/shared/routes/constants";
import { PermissionRequest } from "@p0security/shared/types/permission";
import { RequestStatuses } from "@p0security/shared/types/request-status";
import { Col, Row, Select, Space, Spacing } from "@p0security/ui";
import { GrantToRequestorRecord } from "components/Dashboard/types";
import {
  mockRequestorMap,
  mockRequests,
} from "components/Dashboard/zeroStateData/requests-zero-state";
import {
  directories,
  resourceIntegrations,
} from "components/Integrations/constants";
import { useRequests } from "components/Jit/Requests/hooks/useRequests";
import {
  requestDescription,
  requestLocator,
} from "components/Jit/Requests/requestUtils";
import { formatISO, subDays } from "date-fns";
import { useCallback, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router";

import { Pane } from "../Pane";
import { GrayTitle } from "../common/GrayTitle";
import { PanelErrorBoundary } from "../common/PanelErrorBoundary";
import { FrequentRequestsTable } from "./FrequentRequestsTable";
import { JitSummaryStats } from "./JitSummaryStats";

const DEFAULT_LOOKBACK_DAYS = 30;

const createRequestorMap = (
  requests: { data: PermissionRequest }[],
  lookbackDays: number
): Record<string, GrantToRequestorRecord> => {
  const allIntegrations = [...resourceIntegrations({}), ...directories({})];

  const requestorMap: Record<string, GrantToRequestorRecord> = {};

  for (const { data: requestData } of requests) {
    if (
      new Date(requestData.requestedTimestamp) <
      subDays(new Date(Date.now()), lookbackDays)
    ) {
      continue;
    }

    const { type, requestor } = requestData;

    const location =
      requestLocator(requestData) ||
      allIntegrations.find((i) => i.key === type)?.label;

    // Find end resource for the request
    const resource = requestDescription(requestData)?.replace(/.*\| /, "");

    const serializedKey = `${type}_${location}_${resource}`;

    if (requestor) {
      requestorMap[serializedKey] = requestorMap[serializedKey] || {
        countsByRequestor: {},
        permission: requestData,
      };

      requestorMap[serializedKey].countsByRequestor[requestor] =
        (requestorMap[serializedKey].countsByRequestor[requestor] ?? 0) + 1;

      requestorMap[serializedKey].permission = requestData;
    }
  }

  return requestorMap;
};

export const JitPanels: React.FC<{
  breakpoint: boolean;
  hasRequests: boolean;
}> = ({ breakpoint: lg, hasRequests }) => {
  const { orgSlug } = useParams();

  const [lookbackDays, setLookbackDays] = useState(DEFAULT_LOOKBACK_DAYS);

  const today = new Date();
  const daysAgo = subDays(today, lookbackDays);
  const formattedDate = formatISO(daysAgo);
  const navigate = useNavigate();

  const onNavigateToJit = useCallback(
    () =>
      navigate(
        `/o/${orgSlug}/${AppPaths.AccessManagement}/history?requestedStart=${encodeURIComponent(
          formattedDate
        )}`
      ),
    [navigate, formattedDate, orgSlug]
  );

  // double the lookback days to show change over time period
  const since = useMemo(
    () => subDays(Date.now(), 2 * lookbackDays).getTime(),
    [lookbackDays]
  );
  const { requests, loading } = useRequests(RequestStatuses, { since });

  const requestorMap = useMemo(
    () => createRequestorMap(requests, lookbackDays),
    [requests, lookbackDays]
  );

  const JitSummary = (
    <Pane
      title="Summary"
      showMore={{
        text: "View in Access Management",
        onClick: onNavigateToJit,
      }}
    >
      <PanelErrorBoundary title="Summary">
        <JitSummaryStats
          requestorMap={hasRequests ? requestorMap : mockRequestorMap}
          data={hasRequests ? requests : mockRequests}
          isLoading={loading}
          lookbackDays={lookbackDays}
        />
      </PanelErrorBoundary>
    </Pane>
  );

  const FrequentRequests = (
    <Pane
      title="Frequent Requests"
      showMore={{
        text: "View in Access Management",
        onClick: onNavigateToJit,
      }}
    >
      <PanelErrorBoundary title="Frequent Request">
        <FrequentRequestsTable
          requestorMap={hasRequests ? requestorMap : mockRequestorMap}
          isLoading={loading}
        />
      </PanelErrorBoundary>
    </Pane>
  );

  return (
    <Space direction="vertical" className="w-full">
      <Space className="flex items-center justify-start" size="small">
        <GrayTitle title="Just-in-Time" />
        <Spacing asChild mb="1em">
          <Select
            value={lookbackDays}
            onChange={setLookbackDays}
            /* eslint-disable-line react/forbid-component-props */ style={{
              width: 120,
            }}
            options={[
              { value: 3, label: "3 days" },
              { value: 7, label: "7 days" },
              { value: 14, label: "14 days" },
              { value: 30, label: "30 days" },
              { value: 60, label: "60 days" },
              { value: 90, label: "90 days" },
            ]}
          />
        </Spacing>
      </Space>
      <Row gutter={16}>
        <Col span={lg ? 8 : 24}>{JitSummary}</Col>
        {lg && <Col span={16}>{FrequentRequests}</Col>}
      </Row>
      {!lg && (
        <Row gutter={16}>
          <Col span={24}>{FrequentRequests}</Col>
        </Row>
      )}
    </Space>
  );
};
